import { Loader2 } from 'lucide-react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LocationPickerDialog } from '@/components/LocationPickerDialog';
import { IMAGE_CONSTANTS } from '@/constants/imageConstants';
import { taskcontants } from '@/constants/taskConstants';
import { getAllMoveTasksList, GetTrailersListAsync } from '@/service/MoveTaskService';
import { store } from '@/store';
import { cn } from '@/utils/cn';

import type {
  TrailerMasterData,
  TrailerModel,
  CreateMoveTaskUpdateTaskDetails,
} from '@/service/types/movetasktypes';

// ─────────────────────────────────────────────────────────────────
// Parse TRAILER_MASTER_DATA from settings
// ─────────────────────────────────────────────────────────────────

function getTrailerMasterData(): TrailerMasterData | null {
  const settings = store.getState().global.settings;
  const json = settings?.TRAILER_MASTER_DATA ?? '';
  if (!json) return null;
  try {
    return JSON.parse(json) as TrailerMasterData;
  } catch {
    return null;
  }
}

function getConditionName(conditionCode: string, masterData: TrailerMasterData | null): string {
  if (!conditionCode || !masterData) return conditionCode;
  const match = masterData.Trailer_Conditions_Types.find(
    (c) => String(c.condition_code) === conditionCode
  );
  return match?.condition_name ?? conditionCode;
}

function buildMoveTaskDetails(
  trailer: TrailerModel,
  conditionName: string
): CreateMoveTaskUpdateTaskDetails {
  return {
    CarrierName: trailer.Carrier_Name,
    CarrierCode: trailer.Carrier_scac,
    AlternateCarrierCode: '',
    MoveDestinationType: ' ',
    AlternateDestinationName: ' ',
    TrailerName: trailer.Trailer_Name,
    MoveSourceName: trailer.CurrentLocationName,
    MoveSourceLocationName: trailer.Location,
    AlternateDestinationZone: ' ',
    AlternateTrailerName: ' ',
    MoveDestinationZone: ' ',
    ActionType: taskcontants.TaskActionNew,
    MoveSourceZone: trailer.CurrentLocationZone,
    ExceptionCode: ' ',
    TaskType: taskcontants.MoveTaskType,
    MoveDestinationName: ' ',
    OrgId: store.getState().user.plant?.org_id.toString() ?? '',
    TrailerComments: trailer.TrailerComments,
    MoveSourceType: trailer.CurrentLocationType,
    UserId:
      (store.getState().user.user?.country_code ?? '') +
      (store.getState().user.user?.mobile_no ?? ''),
    TaskComments: ' ',
    AlternateTrailerValid: false,
    TrailerCondition: trailer.TrailerCondition,
    TrailerConditionName: conditionName,
  };
}

// ─────────────────────────────────────────────────────────────────
// Trailer Card
// ─────────────────────────────────────────────────────────────────

interface TrailerCardProps {
  trailer: TrailerModel;
  conditionName: string;
  onMove: (trailer: TrailerModel) => void;
}

function TrailerCard({ trailer, conditionName, onMove }: TrailerCardProps) {
  const { t } = useTranslation();
  const locationDisplay = `${trailer.CurrentLocationZone} - ${trailer.CurrentLocationName} (${trailer.CurrentLocationType})`;

  const isDamaged = trailer.IsTrailerDamaged || conditionName.toUpperCase() === 'DAMAGED';

  const handleMove = useCallback(() => {
    onMove(trailer);
  }, [onMove, trailer]);

  return (
    <div className="mx-[10px] rounded-lg border border-task-border bg-white p-4">
      <div className="flex gap-4">
        {/* Trailer icon */}
        <div className="flex-shrink-0 pt-1">
          <img src={IMAGE_CONSTANTS.trailerCard} alt="" className="h-10 w-12" aria-hidden="true" />
        </div>

        {/* Trailer details */}
        <div className="flex-1 min-w-0">
          {/* Location */}
          <div className="mb-2">
            <span className="text-xs font-poppins text-task-label">
              {t('createMoveTask.location')}
            </span>
            <span className="text-xs font-poppins font-bold text-task-details">
              {locationDisplay}
            </span>
          </div>

          {/* Trailer Number, Carrier Name, Condition */}
          <div className="flex flex-wrap gap-x-8 gap-y-1">
            <div>
              <p className="text-xs font-poppins text-task-label">
                {t('createMoveTask.trailerNumber')}
              </p>
              <p className="text-xs font-poppins font-bold text-task-details">
                {trailer.Trailer_Name}
              </p>
            </div>
            <div>
              <p className="text-xs font-poppins text-task-label">
                {t('createMoveTask.carrierName')}
              </p>
              <p className="text-xs font-poppins font-bold text-task-details">
                {trailer.Carrier_Name}
              </p>
            </div>
            <div>
              <p className="text-xs font-poppins text-task-label">
                {t('createMoveTask.condition')}
              </p>
              <p
                className={cn(
                  'text-xs font-poppins font-bold',
                  isDamaged ? 'text-[#BE0000]' : 'text-task-details'
                )}
              >
                {conditionName}
              </p>
            </div>
          </div>
        </div>
      </div>

      {!isDamaged && (
        <div className="flex justify-end mt-3">
          <button
            type="button"
            onClick={handleMove}
            className="h-9 w-[120px] rounded-md bg-move-task text-white text-sm font-poppins font-semibold"
          >
            {t('createMoveTask.move')}
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export function CreateMoveTaskPage() {
  const { t } = useTranslation();
  const [trailers, setTrailers] = useState<TrailerModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [masterData, setMasterData] = useState<TrailerMasterData | null>(null);
  const [selectedTask, setSelectedTask] = useState<CreateMoveTaskUpdateTaskDetails | null>(null);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);

  const loadTrailers = useCallback(async () => {
    setLoading(true);
    try {
      const tempmasterdata = getTrailerMasterData();
      setMasterData(tempmasterdata);

      const [trailerList, filteredMoveTaskList] = await Promise.all([
        GetTrailersListAsync(),
        getAllMoveTasksList(),
      ]);

      const finalList: TrailerModel[] = [];

      for (const trailer of trailerList) {
        if (
          filteredMoveTaskList.filter(
            (x) => x.MoveSourceName.toUpperCase() === trailer.CurrentLocationName.toUpperCase()
          ).length === 0
        ) {
          if (
            trailer.TrailerCondition !== '' &&
            tempmasterdata?.Trailer_Conditions_Types.length !== 0
          ) {
            trailer.TrailerConditionName =
              tempmasterdata?.Trailer_Conditions_Types.find(
                (a) => String(a.condition_code) === trailer.TrailerCondition
              )?.condition_name ?? '';
          } else {
            trailer.TrailerConditionName = '';
          }
          finalList.push(trailer);
        }
      }

      setTrailers(finalList);
      setSearchQuery(' ');
    } catch {
      setTrailers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTrailers();
  }, [loadTrailers]);

  // Matches Xamarin: only Yard-type locations, exclude UNL zone, require non-empty fields
  const baseFilteredTrailers = useMemo(
    () =>
      trailers.filter((t) => {
        if (!t.CurrentLocationType || !t.CurrentLocationZone) return false;
        if (t.CurrentLocationType.toUpperCase() !== 'YARD') return false;
        if (t.CurrentLocationZone.toUpperCase() === 'UNL') return false;
        return true;
      }),
    [trailers]
  );

  // Filter trailers by search
  const filteredTrailers = useMemo(() => {
    if (!searchQuery.trim()) return baseFilteredTrailers;
    const q = searchQuery.toLowerCase().trim();
    return baseFilteredTrailers.filter(
      (t) =>
        t.Trailer_Name.toLowerCase().includes(q) ||
        t.Carrier_Name.toLowerCase().includes(q) ||
        t.CurrentLocationName.toLowerCase().includes(q) ||
        t.CurrentLocationZone.toLowerCase().includes(q)
    );
  }, [baseFilteredTrailers, searchQuery]);

  // Build the full move trailer list for LocationPickerDialog (uses base-filtered list)
  const allMoveTrailerList = useMemo(
    () =>
      baseFilteredTrailers.map((t) =>
        buildMoveTaskDetails(t, getConditionName(t.TrailerCondition, masterData))
      ),
    [baseFilteredTrailers, masterData]
  );

  function handleMoveClick(trailer: TrailerModel) {
    const taskDetails = buildMoveTaskDetails(
      trailer,
      getConditionName(trailer.TrailerCondition, masterData)
    );
    setSelectedTask(taskDetails);
    setLocationDialogOpen(true);
  }

  function handleCloseDialog() {
    setLocationDialogOpen(false);
    setSelectedTask(null);
  }

  // Refresh trailer list after successful move task creation — matches Xamarin behavior
  const handleMoveSuccess = useCallback(() => {
    void loadTrailers();
  }, [loadTrailers]);

  return (
    <div className="flex flex-col h-full">
      {/* Search bar */}
      <div className="py-2">
        <div className="mx-[10px] rounded-[3px] border border-searchbar-border bg-white">
          <input
            type="text"
            placeholder={t('createMoveTask.search')}
            value={searchQuery}
            maxLength={100}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            className="h-10 w-full bg-transparent pl-[10px] pr-2 text-xs font-poppins text-task-details placeholder:text-task-label outline-none"
          />
        </div>
      </div>

      {/* Trailer list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-button" />
          </div>
        ) : filteredTrailers.length > 0 ? (
          <div className="space-y-3 pb-4">
            {filteredTrailers.map((trailer) => (
              <TrailerCard
                key={trailer.Trailer_Name}
                trailer={trailer}
                conditionName={getConditionName(trailer.TrailerCondition, masterData)}
                onMove={handleMoveClick}
              />
            ))}
          </div>
        ) : (
          <p className="text-center text-sm font-poppins text-task-label py-16">
            {searchQuery.trim()
              ? t('createMoveTask.noTrailersMatch')
              : t('createMoveTask.noTrailersAvailable')}
          </p>
        )}
      </div>

      {/* Location Picker Dialog */}
      <LocationPickerDialog
        open={locationDialogOpen}
        task={selectedTask}
        onClose={handleCloseDialog}
        onSuccess={handleMoveSuccess}
        allMoveTrailerList={allMoveTrailerList}
      />
    </div>
  );
}
